import { Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE } from "@/lib/site";

// Dark editorial Audiences section — visually inspired by Flyer 1
const Audiences = () => {
    const { t } = useLanguage();

    const imageKeys = {
        mothers: SITE.images.mothers,
        men: SITE.images.men,
        families: SITE.images.families,
        strugglers: SITE.images.strugglers,
        seekers: SITE.images.seekers,
    };

    return (
        <section
            id="audiences"
            data-testid="audiences-section"
            className="relative py-24 md:py-32 bg-ella-charcoal text-ella-cream overflow-hidden grain"
        >
            <div className="absolute -top-40 left-1/3 w-[560px] h-[560px] rounded-full bg-[radial-gradient(circle_at_center,rgba(193,154,107,0.10),transparent_60%)] pointer-events-none" />

            <div className="ella-container relative">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-3">
                        <span className="gold-hairline" />
                        <span className="ella-overline-light">{t.audiences.overline}</span>
                        <span className="gold-hairline" />
                    </div>
                    <h2
                        className="ella-display text-4xl md:text-5xl lg:text-6xl mt-5 text-ella-cream"
                        data-testid="audiences-title"
                    >
                        {t.audiences.title}
                    </h2>
                    <p className="mt-6 text-lg text-ella-cream/70 font-light">
                        {t.audiences.subtitle}
                    </p>
                </div>

                <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                    {t.audiences.items.map((item, i) => (
                        <article
                            key={item.id}
                            data-testid={`audience-${item.id}`}
                            className="group relative rounded-2xl overflow-hidden border border-ella-line-dark bg-ella-onyx/70 hover:border-ella-gold/60 transition-all duration-700 flex flex-col"
                        >
                            <div className="aspect-[4/5] overflow-hidden">
                                <img
                                    src={imageKeys[item.id]}
                                    alt={item.label}
                                    className="w-full h-full object-cover opacity-70 group-hover:opacity-95 group-hover:scale-105 transition-all duration-1000"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ella-charcoal via-ella-charcoal/40 to-transparent pointer-events-none" />
                                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-ella-onyx/80 backdrop-blur border border-ella-gold/40">
                                    <span className="text-[10px] uppercase tracking-[0.22em] text-ella-champagne">
                                        0{i + 1}
                                    </span>
                                </div>
                            </div>

                            <div className="p-6 flex-1 flex flex-col">
                                <h3 className="font-display text-2xl text-ella-cream">
                                    {item.label}
                                </h3>
                                <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-ella-gold">
                                    {item.headline}
                                </p>
                                <p className="mt-4 text-sm text-ella-cream/70 leading-relaxed font-light flex-1">
                                    {item.description}
                                </p>
                                <ul className="mt-5 space-y-2.5">
                                    {item.points.map((p, j) => (
                                        <li key={j} className="flex items-start gap-2.5 text-sm text-ella-cream/85">
                                            <Check className="w-3.5 h-3.5 mt-1 text-ella-gold flex-shrink-0" strokeWidth={2} />
                                            <span>{p}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="mt-16 text-center">
                    <p
                        className="font-display italic text-xl md:text-2xl text-ella-champagne max-w-3xl mx-auto"
                        data-testid="audiences-closer"
                    >
                        “{t.audiences.closer}”
                    </p>
                </div>
            </div>
        </section>
    );
};

export default Audiences;
